// Analysis on top of a parsed Apple 2703(d) return (see apple2703d.js): pattern
// of life, activity gaps, an incident window, IP analysis, contact ranking,
// links between accounts, a device timeline and account security events.
//
// Everything works from one list of "events": every dated record from every
// sheet, with the IPs, contact, device and iOS version pulled out of it.
// Inferences ("likely home IP") are leads with their evidence attached, never facts.

import { isIp, cleanHandle, modelName, parseAppleTime } from './apple2703d';
import {
  zonedParts, zonedToEpoch, patternOfLife, activityGaps, incidentWindow, ipv6Network, ipProfile, homeAndWork,
  ipv6Networks, handleKey, phoneKey, contactRanking, sharedLinks, newIpChecker,
} from './analysisKit';

// The generic pieces live in analysisKit.js; re-exported so existing imports keep working.
export { zonedParts, zonedToEpoch, patternOfLife, activityGaps, incidentWindow, ipv6Network, ipProfile, homeAndWork, ipv6Networks, handleKey };

// Sheets that record something the account did. Account creation dates,
// device registrations and inbound mail are dated, but aren't activity.
export const ACTIVITY_KINDS = new Set([
  'icloudLogs', 'imessageQuery', 'facetimeQuery', 'facetimeInvites', 'mailLogs',
  'signOn', 'transactions', 'downloads', 'appleIdActivity', 'gameCenter',
]);

// ─── Events ──────────────────────────────────────────────────────────────────

// iOS version / build from the device strings Apple logs:
// "21A360; iPhone15,3" · "<iPhone15,3> <iPhone OS;17.0.3;21A360> …" · "iPhone_OS_17_0_3_21A360"
export function parseOs(s) {
  const v = String(s || '');
  let m = v.match(/(iPhone|iPad) ?OS;(\d+(?:\.\d+)*);(\w+)/i);
  if (m) return { version: m[2], build: m[3] };
  m = v.match(/(?:iPhone|iPad)_OS_(\d+(?:_\d+)*)_([0-9]{2}[A-Z]\w*)/i);
  if (m) return { version: m[1].replace(/_/g, '.'), build: m[2] };
  m = v.match(/\b(\d{2}[A-Z]\d{1,4}[a-z]?)\b/);
  return m ? { version: '', build: m[1] } : null;
}

const MODEL_RE = /(iPhone|iPad|iPod|Watch|MacBookPro|MacBookAir|MacBook|iMac|Macmini|Mac)\d+[,_]\d+/g;

// One flat list of dated records with what's useful pulled out of each.
export function buildEvents(model) {
  const events = [];
  for (const d of model.datasets) {
    if (d.timeCol < 0) continue;
    const h = d.header.map(x => x.toLowerCase());
    const iHandle = h.findIndex(x => /lookup handle|recipient handle/.test(x));
    const deviceCols = h.map((_, i) => i).filter(i => /device|model|^os$|platform|client/.test(h[i]));
    const show = d.header.map((_, i) => i).filter(i => i !== d.timeCol && i !== d.dsidCol);
    d.rows.forEach((r, i) => {
      const t = d.times[i];
      if (!t) return;
      const deviceText = deviceCols.map(c => r[c]).join(' ');
      events.push({
        t: t.t, dateOnly: t.dateOnly, raw: r[t.col], account: d.rowAccounts[i],
        kind: d.kind, source: d.label, datasetId: d.id, row: i,
        activity: ACTIVITY_KINDS.has(d.kind) && !t.dateOnly,
        ips: d.ipCols.map(c => r[c]).filter(isIp),
        handle: iHandle >= 0 ? cleanHandle(r[iHandle]) : '',
        models: [...new Set((deviceText.match(MODEL_RE) || []).map(x => x.replace('_', ',')))],
        os: parseOs(deviceText),
        detail: show.filter(c => r[c] && r[c] !== '0').slice(0, 6).map(c => `${d.header[c]}: ${r[c]}`).join(' · '),
      });
    });
  }
  return events.sort((a, b) => a.t - b.t);
}

// ─── 4. Contacts (iMessage / FaceTime lookups) ───────────────────────────────

// The account's own phone numbers and emails, so lookups of itself can be set apart.
export function ownHandleKeys(accounts) {
  const own = new Map();
  for (const a of accounts) {
    const keys = new Set([...a.emails, ...a.phones, a.appleId].filter(Boolean).map(handleKey));
    (a.profile || []).forEach(([k, v]) => {
      if (/phone/i.test(k)) (String(v).match(/\+?\d[\d ()-]{8,}\d/g) || []).forEach(p => keys.add(phoneKey(p)));
      if (/email|apple id/i.test(k)) (String(v).match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g) || []).forEach(m => keys.add(m.toLowerCase()));
    });
    own.set(a.key, keys);
  }
  return own;
}

export function contactProfile(events, accounts, tz = 'UTC') {
  return contactRanking(events, ownHandleKeys(accounts), e => (e.kind === 'imessageQuery' ? 'iMessage' : 'FaceTime'), tz);
}

// ─── 5. Links between accounts, and the device timeline ──────────────────────

export function accountLinks({ accounts, ipProf, contacts, devices, production }) {
  const targets = accounts.map(a => a.key).filter(k => k !== production);
  const nets = new Map();
  ipProf.filter(x => x.network).forEach(x => nets.set(x.network, [...new Set([...(nets.get(x.network) || []), ...x.accounts])]));
  return sharedLinks(targets, {
    ips: ipProf.map(x => ({ label: x.ip, accounts: x.accounts })),
    networks: [...nets.entries()].map(([label, accts]) => ({ label, accounts: accts })),
    devices: [
      ...devices.serials.map(d => ({ label: `serial ${d.serial}`, accounts: d.accounts })),
      ...(devices.guids || []).map(g => ({ label: `device ID ${g.guid}`, accounts: g.accounts })),
    ],
    contacts: contacts.filter(c => !c.own).map(c => ({ label: c.handle, accounts: c.accounts })),
  });
}

// Each device model and iOS version: when it first and last appears.
export function deviceTimeline(events) {
  const map = new Map();
  for (const e of events) {
    if (e.dateOnly || (!e.models.length && !e.os)) continue;
    const models = e.models.length ? e.models : [''];
    for (const m of models) {
      const os = e.os ? (e.os.version ? `iOS ${e.os.version}${e.os.build ? ` (${e.os.build})` : ''}` : `build ${e.os.build}`) : '';
      const key = `${m}|${e.os?.build || e.os?.version || ''}`;
      if (!map.has(key)) map.set(key, { model: m, name: modelName(m), os, build: e.os?.build || '', count: 0, first: e.t, last: e.t, accounts: new Set(), sources: new Set() });
      const x = map.get(key);
      if (!x.os.startsWith('iOS') && os.startsWith('iOS')) x.os = os;
      x.count++;
      x.first = Math.min(x.first, e.t);
      x.last = Math.max(x.last, e.t);
      x.accounts.add(e.account);
      x.sources.add(e.source);
    }
  }
  // A bare build ("21A360") and a full version with the same build are one row.
  const rows = [...map.values()];
  return rows.map(x => ({ ...x, accounts: [...x.accounts], sources: [...x.sources] }))
    .sort((a, b) => a.first - b.first);
}

// ─── 6. Account security events ──────────────────────────────────────────────

// "New IP" (see newIpChecker): the account hadn't used that IP more than 24 hours
// before the event. Not flagged on account creation, when every IP is new.
export function securityEvents(model, events) {
  const check = newIpChecker(events);
  const out = [];
  const push = (t, account, type, detail, ip) => {
    const c = check(account, ip, t);
    const creation = /created|signed up/i.test(type);
    out.push({ t, account, type, detail, ip: ip || '', seen: c.seen, newIp: !!(ip && !creation && c.newIp) });
  };
  for (const d of model.datasets) {
    const h = d.header.map(x => x.toLowerCase());
    const col = re => h.findIndex(x => re.test(x));
    const ipOf = r => d.ipCols.map(c => r[c]).find(isIp) || '';
    if (d.kind === 'appleIdActivity') {
      const app = col(/app_name/), err = col(/error/);
      d.rows.forEach((r, i) => d.times[i] && push(d.times[i].t, d.rowAccounts[i], 'Apple ID / iForgot activity',
        [r[app], r[err]].filter(Boolean).join(' · ') || 'Account management or password reset site', ipOf(r)));
    }
    if (d.kind === 'signOn') {
      const failed = col(/failed/), app = col(/app_name/);
      d.rows.forEach((r, i) => {
        if (failed >= 0 && d.times[i] && r[failed] && r[failed] !== '0') {
          push(d.times[i].t, d.rowAccounts[i], 'Failed sign-in', r[app] || '', ipOf(r));
        }
      });
    }
    const timeAt = (r, c) => (c >= 0 && r[c] ? parseAppleTime(r[c], d.meta.timezone) : null);
    if (d.kind === 'subscriber') {
      const billing = col(/billing_profile_create_ts/), signup = col(/signup_ts/);
      const card = col(/credit_card_name/), billingIp = col(/billing_profile_create_ip/);
      d.rows.forEach((r, i) => {
        const tb = timeAt(r, billing), ts = timeAt(r, signup);
        if (tb) push(tb.t, d.rowAccounts[i], 'Billing profile created', r[card] || '', isIp(r[billingIp]) ? r[billingIp] : '');
        if (ts) push(ts.t, d.rowAccounts[i], 'Signed up for Apple Media Services', '', '');
      });
    }
    if (d.kind === 'account' && d.rows[0]) {
      const r = d.rows[0];
      const regIp = r[col(/registration ip/)] || '';
      const t = timeAt(r, col(/apple id creation date/));
      if (t) push(t.t, d.account, 'Apple ID created', 'From the Account Details sheet', isIp(regIp) ? regIp : '');
    }
  }
  return out.sort((a, b) => a.t - b.t);
}
